import type { M49Area } from "@baton/api";
import { ChevronRight, Globe } from "@baton/icons";
import { useId, type ReactNode } from "react";
import { Link } from "react-router";
import { flagOf } from "../../accounts/CountryFlag.tsx";

/**
 * A collapsible heading over a run of sidebar rows, with the count of what it holds at its end:
 * a client over its projects in Work's Projects branch (`ClientHeadings.tsx`), and a country over
 * its clients in the Clients mode (`ClientsSidebar.tsx`), the country's flag before its name, or the
 * globe before the name of an area over the clients located by it alone (020 FR-020-262). The flag or
 * the globe sits in a mark slot of its own width and the rows under such a heading start where its name
 * does; the count stands in the rows' trailing slot (2026-10-03, `../shell.css`). The caller keeps which
 * headings are collapsed, because each stores them under a key of its own. A heading the Tickets mode
 * draws over a country's or an area's clients is also a link to that place's tickets (028 FR-028-071,
 * 2026-10-24): its chevron is then a toggle of its own beside the link, so the row is one link and the
 * toggle one button, as a branching row's disclosure is.
 */
export interface HeadingGroupProps {
  readonly name: string;
  /** The ISO 3166-1 alpha-2 code of a country heading, drawn as its flag before the name, which already names it for a screen reader. */
  readonly country?: string | null;
  /** The area of a heading of the clients located by it alone, drawn as the globe before the name. */
  readonly area?: M49Area | null;
  readonly count: number;
  /** The count in words, for a screen reader. */
  readonly countLabel: string;
  readonly open: boolean;
  readonly onToggle: () => void;
  /** The heading as a link to its place's page, with the toggle's own name and whether the page is the current one. */
  readonly link?: { readonly to: string; readonly current: boolean; readonly toggleLabel: string; readonly onNavigate?: () => void };
  readonly children: ReactNode;
}

export function HeadingGroup({ name, country, area, count, countLabel, open, onToggle, link, children }: HeadingGroupProps) {
  const listId = useId();
  const face = (
    <>
      {country !== undefined && country !== null && (
        <span className="b-sidebar__client-flag" aria-hidden="true">
          {flagOf(country)}
        </span>
      )}
      {area !== undefined && area !== null && (
        <span className="b-sidebar__client-flag" aria-hidden="true">
          <Globe />
        </span>
      )}
      <span className="b-sidebar__client-name">{name}</span>
      <span className="b-sidebar__trailing b-sidebar__count">
        <span aria-hidden="true">{count}</span>
        <span className="sr-only">{countLabel}</span>
      </span>
    </>
  );
  return (
    <section className="b-sidebar__client" data-mark={(country ?? area ?? null) === null ? undefined : ""} aria-label={name}>
      {link === undefined ? (
        <button type="button" className="b-sidebar__client-heading" aria-expanded={open} aria-controls={listId} onClick={onToggle}>
          <ChevronRight aria-hidden="true" />
          {face}
        </button>
      ) : (
        <div className="b-sidebar__client-heading" data-link="true">
          <button type="button" className="b-sidebar__client-toggle" aria-expanded={open} aria-controls={listId} aria-label={link.toggleLabel} onClick={onToggle}>
            <ChevronRight aria-hidden="true" />
          </button>
          <Link to={link.to} className="b-sidebar__client-link" aria-current={link.current ? "page" : undefined} onClick={link.onNavigate}>
            {face}
          </Link>
        </div>
      )}
      <div id={listId} hidden={!open}>
        {children}
      </div>
    </section>
  );
}
